import { AlertTriangleIcon, RotateCcwIcon, Undo2Icon } from 'lucide-react';
import type {
  DocumentGenerationProgressDTO,
  DocumentGenerationStatusResponseDTO,
} from '@shared/schemas/document-generation';
import { Button } from '@/components/ui/button';
import { getChatErrorMessage } from '../chat-errors';
import { GenerationProgress } from './GenerationProgress';
import './document-canvas.css';

type CanvasBannersProps = {
  generationId?: string;
  /** Retour à la version précédente, proposé quand la nouvelle version a échoué. */
  onRevert: (() => void) | null;
  onRetryPolling: () => void;
  pollingExhausted: boolean;
  progress: DocumentGenerationProgressDTO | undefined;
  status: DocumentGenerationStatusResponseDTO | undefined;
};

export function CanvasBanners({
  generationId,
  onRetryPolling,
  onRevert,
  pollingExhausted,
  progress,
  status,
}: CanvasBannersProps) {
  const isInProgress = status?.status === 'pending' || status?.status === 'generating';

  if (status && (status.status === 'failed' || status.status === 'dead')) {
    return (
      <div
        className="canvas-banner-in flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-destructive/20 bg-destructive/5 px-4 py-2.5 text-sm"
        role="alert"
      >
        <AlertTriangleIcon className="size-4 shrink-0 text-destructive" aria-hidden />
        <span className="min-w-0 flex-1 text-destructive">
          {getChatErrorMessage({ code: status.errorCode })}
        </span>
        {onRevert ? (
          <Button className="h-7" onClick={onRevert} size="sm" type="button" variant="outline">
            <Undo2Icon className="size-3.5" aria-hidden />
            Revenir à la version précédente
          </Button>
        ) : null}
      </div>
    );
  }

  if (pollingExhausted && (isInProgress || !status)) {
    return (
      <div className="canvas-banner-in flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-amber-500/25 bg-amber-500/[0.07] px-4 py-2.5 text-sm" role="status">
        <AlertTriangleIcon className="size-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
        <span className="min-w-0 flex-1">La génération prend plus de temps que prévu.</span>
        <Button className="h-7" onClick={onRetryPolling} size="sm" type="button" variant="outline">
          <RotateCcwIcon className="size-3.5" aria-hidden />
          Réessayer
        </Button>
      </div>
    );
  }

  if (isInProgress) {
    return (
      <div className="canvas-banner-in border-b border-border/50 bg-background px-4 py-2.5">
        <GenerationProgress density="compact" generationId={generationId} progress={progress} status={status?.status} />
      </div>
    );
  }

  return null;
}
